import React from 'react';
import { X, Filter, RotateCcw, Check } from 'lucide-react';

const FeeAdvancedFiltersDrawer = ({
  isOpen,
  onClose,
  classes = [],
  feeStructures = [],
  filters,
  onFilterChange,
  onResetFilters,
  customMaxDueAmount,
  setCustomMaxDueAmount,
  customMinDueAmount,
  setCustomMinDueAmount,
  totalFilteredCount = 0
}) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(3px)',
        zIndex: 1050,
        display: 'flex',
        justifyContent: 'flex-end',
        transition: 'all 0.2s ease-in-out'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '380px',
          height: '100%',
          backgroundColor: '#ffffff',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1051
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f8fafc'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Filter size={18} strokeWidth={2.2} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                Advanced Filters
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                Refine fee records across multiple parameters
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Drawer Body - Filter Controls */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Class Filter */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Class
            </label>
            <select
              className="form-control"
              value={filters.class || ''}
              onChange={(e) => onFilterChange('class', e.target.value)}
              style={{ height: '34px', fontSize: '12.5px', borderRadius: '6px' }}
            >
              <option value="">All Classes</option>
              {classes.map((cls) => (
                <option key={cls} value={cls}>
                  {cls}
                </option>
              ))}
            </select>
          </div>

          {/* Section Filter */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Section
            </label>
            <select
              className="form-control"
              value={filters.section || ''}
              onChange={(e) => onFilterChange('section', e.target.value)}
              style={{ height: '34px', fontSize: '12.5px', borderRadius: '6px' }}
            >
              <option value="">All Sections</option>
              {['A', 'B', 'C', 'D', 'E'].map((sec) => (
                <option key={sec} value={sec}>
                  Section {sec}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Status */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Payment Status
            </label>
            <select
              className="form-control"
              value={filters.paymentStatus || ''}
              onChange={(e) => onFilterChange('paymentStatus', e.target.value)}
              style={{ height: '34px', fontSize: '12.5px', borderRadius: '6px' }}
            >
              <option value="">All Statuses</option>
              <option value="PAID">✓ FULLY PAID</option>
              <option value="PARTIAL">PARTIALLY PAID</option>
              <option value="UNPAID">UNPAID</option>
              <option value="OVERDUE">OVERDUE</option>
              <option value="NOT_ASSIGNED">NOT ASSIGNED</option>
              <option value="VOIDED">VOIDED RECORDS</option>
            </select>
          </div>

          {/* Due Amount Presets */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Due Amount Range
            </label>
            <select
              className="form-control"
              value={filters.dueAmountFilter || ''}
              onChange={(e) => onFilterChange('dueAmountFilter', e.target.value)}
              style={{ height: '34px', fontSize: '12.5px', borderRadius: '6px' }}
            >
              <option value="">Any Due Amount</option>
              <option value="FULLY_PAID">✓ Fully Paid (₹0 Due)</option>
              <option value="LESS_2000">Due &lt; ₹2,000</option>
              <option value="LESS_5000">Due &lt; ₹5,000</option>
              <option value="MORE_5000">Due &gt; ₹5,000</option>
              <option value="MORE_10000">Due &gt; ₹10,000</option>
            </select>
          </div>

          {/* Custom Min / Max Due Amount */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                Min Due (₹)
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                value={customMinDueAmount || ''}
                onChange={(e) => setCustomMinDueAmount(e.target.value)}
                style={{ height: '34px', fontSize: '12.5px', borderRadius: '6px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                Max Due (₹)
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Unlimited"
                value={customMaxDueAmount || ''}
                onChange={(e) => setCustomMaxDueAmount(e.target.value)}
                style={{ height: '34px', fontSize: '12.5px', borderRadius: '6px' }}
              />
            </div>
          </div>

          {/* Assigned Fee Structure */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Fee Structure Template
            </label>
            <select
              className="form-control"
              value={filters.feeStructureId || ''}
              onChange={(e) => onFilterChange('feeStructureId', e.target.value)}
              style={{ height: '34px', fontSize: '12.5px', borderRadius: '6px' }}
            >
              <option value="">All Structures</option>
              {feeStructures.map((fs) => (
                <option key={fs.id} value={fs.id}>
                  {fs.name} (₹{Number(fs.totalAmount || 0).toLocaleString('en-IN')})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Drawer Footer */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px'
          }}
        >
          <button
            type="button"
            className="btn btn-light"
            onClick={onResetFilters}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '34px',
              fontSize: '12px',
              fontWeight: '600',
              border: '1px solid #cbd5e1'
            }}
          >
            <RotateCcw size={14} /> Reset
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onClose}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '34px',
              fontSize: '12px',
              fontWeight: '600',
              padding: '0 16px'
            }}
          >
            <Check size={15} /> Apply ({totalFilteredCount} Matches)
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeeAdvancedFiltersDrawer;
